import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from './config/apiConfig';
import OrderDesign from './OrderDesign';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import { LegendaryOrderRevealVfx } from './effects/legendaryDeployVfx';
import { playSoundEffect } from './hooks/useSoundEffects';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import { resolvePublicAssetBackgroundUrl } from './utils/resolvePublicAssetUrl';
import { buildOrderDetailLangQuery } from './utils/buildOrderDetailLangQuery';
import MenuPaintedButton from './components/MenuPaintedButton';
import {
  MENU_PAINTED_SURFACE_FILTER,
  WAR_EFFORT_FACTION_BUTTON_PATH,
  WAR_EFFORT_PRIMARY_BUTTON_PATH,
} from './config/welcomeAssets';
import './WarEffortSkeletonCrewSelection.css';

const SKELETON_CREW_SIZE = 30;
const PICK_TIME_MS = 20000;
const BASE_OFFER_CARD_SIZE = 390;
const BASE_OFFER_CARD_SCALE = 1.56;
const RARITY_PROBS = { common: 0.5, rare: 0.3, epic: 0.12, legendary: 0.08 };
const RARITIES = ['common', 'rare', 'epic', 'legendary'];

function getSkeletonOfferLayout(viewportWidth, viewportHeight, offersBoxHeight = 0) {
  const vw = viewportWidth;
  const vh = viewportHeight || 700;
  const isCompact = vw <= 1024;

  if (!isCompact) {
    return {
      cardSize: BASE_OFFER_CARD_SIZE,
      stacked: false,
      scroll: false,
      gap: 40,
      compact: false,
    };
  }

  const horizontalPadding = vw <= 480 ? 12 : 16;
  const gap = vw <= 480 ? 4 : 6;
  const gapsTotal = gap * 2;
  const chromeFallback = vw <= 480 ? 148 : 162;
  const cardSizeByWidth = Math.floor((vw - horizontalPadding - gapsTotal) / 3);
  const cardSizeByHeight = offersBoxHeight > 0
    ? Math.floor(offersBoxHeight)
    : Math.floor(vh - chromeFallback);
  const cardSize = Math.max(
    72,
    Math.min(BASE_OFFER_CARD_SIZE, cardSizeByWidth, cardSizeByHeight)
  );

  return {
    cardSize,
    stacked: false,
    scroll: false,
    gap,
    compact: true,
  };
}

function SkeletonCrewOrderCard({ order, cardSize }) {
  if (!order) {
    throw new Error('SkeletonCrewOrderCard: order is required');
  }
  const rarity = (order.rarity || 'common').toLowerCase();
  const visualScale = cardSize / BASE_OFFER_CARD_SIZE;

  return (
    <div
      className="skeleton-crew-order-card-frame"
      style={{
        width: cardSize,
        height: cardSize,
        ['--skeleton-crew-visual-scale']: visualScale,
      }}
    >
      <div className="skeleton-crew-order-card-scaler">
        <div className={`reward-order-container skeleton-crew-order-card-inner rarity-${rarity}`}>
          <OrderDesign
            order={order}
            count={1}
            scale={BASE_OFFER_CARD_SCALE}
            rarity={order.rarity}
            noMargin={true}
            gameState={null}
          />
          <OrderCardBubbles rarity={order.rarity || 'common'} />
        </div>
      </div>
    </div>
  );
}

/** With blue/dark overlay – used on "Continue with your skeleton crew" page */
export function getSkeletonCrewBgStyle() {
  return {
    background: `linear-gradient(to bottom, rgba(2, 10, 16, 0.85) 0%, rgba(2, 10, 16, 0.9) 100%), ${resolvePublicAssetBackgroundUrl('/images/skeleton_crew.jpg')} center center / cover no-repeat`,
  };
}

/** Image only, no filter – used on skeleton crew selection page */
function getSkeletonCrewBgStyleNoFilter() {
  return {
    background: `${resolvePublicAssetBackgroundUrl('/images/skeleton_crew.jpg')} center center / cover no-repeat`,
  };
}

function pickByRarity(ordersByRarity) {
  const r = Math.random();
  let acc = 0;
  for (const rarity of RARITIES) {
    acc += RARITY_PROBS[rarity];
    if (r < acc) {
      const pool = ordersByRarity[rarity];
      if (pool && pool.length > 0) return pool[Math.floor(Math.random() * pool.length)];
      break;
    }
  }
  const fallback = RARITIES.find(r => ordersByRarity[r]?.length > 0);
  const pool = fallback ? ordersByRarity[fallback] : [];
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Build 30 random order IDs from the pool (respecting max 3 per order, 1 for unique). Returns null if pool can't fill 30. */
function buildRandomCrew(ordersByRarity) {
  const countInCrew = {};
  const result = [];
  for (let i = 0; i < SKELETON_CREW_SIZE; i++) {
    const canAdd = (o) => {
      const c = countInCrew[o.id] || 0;
      const max = o.is_unique === true ? 1 : 3;
      return c < max;
    };
    const available = [
      ...(ordersByRarity.common || []).filter(canAdd),
      ...(ordersByRarity.rare || []).filter(canAdd),
      ...(ordersByRarity.epic || []).filter(canAdd),
      ...(ordersByRarity.legendary || []).filter(canAdd)
    ];
    if (available.length === 0) return null;
    const order = available[Math.floor(Math.random() * available.length)];
    result.push(order.id);
    countInCrew[order.id] = (countInCrew[order.id] || 0) + 1;
  }
  return result;
}

function WarEffortSkeletonCrewSelection({
  purpose,
  selectedFaction,
  onComplete,
  onBack,
  isSubmitting = false,
  submitError = null,
  embedded = false,
  backgroundStyle = getSkeletonCrewBgStyleNoFilter(),
}) {
  const { t, i18n: i18nInstance } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [ordersByRarity, setOrdersByRarity] = useState({ common: [], rare: [], epic: [], legendary: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skeletonCrew, setSkeletonCrew] = useState([]);
  const [offeredOrders, setOfferedOrders] = useState([]);
  const [timeLeft, setTimeLeft] = useState(PICK_TIME_MS / 1000);
  const [animatingOrderId, setAnimatingOrderId] = useState(null);
  const [flyAnimation, setFlyAnimation] = useState(null);
  const timerRef = useRef(null);
  const pickedThisRoundRef = useRef(false);
  const timeRunningOutPlayedRef = useRef(false);
  const timeRunningOutAudioRef = useRef(null);
  const offerablePoolEmptyRef = useRef(false);
  const counterRef = useRef(null);
  const offerRefs = useRef({});
  const offersContainerRef = useRef(null);
  const [offersBoxHeight, setOffersBoxHeight] = useState(0);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  useEffect(() => {
    const onResize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const node = offersContainerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextHeight = Math.floor(entry.contentRect.height);
      setOffersBoxHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, skeletonCrew.length, offeredOrders.length]);

  const offerLayout = useMemo(
    () => getSkeletonOfferLayout(viewportSize.width, viewportSize.height, offersBoxHeight),
    [viewportSize.width, viewportSize.height, offersBoxHeight]
  );

  const fetchOrders = useCallback(async () => {
    if (!selectedFaction) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const langQuery = buildOrderDetailLangQuery(i18nInstance.language);
      const res = await fetch(
        `${API_URL}/orders?faction=${encodeURIComponent(selectedFaction)}&${langQuery}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(t('warEffort.skeletonCrew.failedToLoadOrders'));
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || t('warEffort.skeletonCrew.failedToLoadOrders'));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFaction, t, i18nInstance.language]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (orders.length === 0) return;
    const byRarity = { common: [], rare: [], epic: [], legendary: [] };
    orders.forEach(o => {
      if ((o.availability || '').toLowerCase() === 'created') return;
      const r = (o.rarity || 'common').toString().toLowerCase();
      const key = RARITIES.includes(r) ? r : 'common';
      byRarity[key].push(o);
    });
    setOrdersByRarity(byRarity);
  }, [orders]);

  const offerableCount = useMemo(
    () => orders.filter(o => (o.availability || '').toLowerCase() !== 'created').length,
    [orders]
  );

  const handleRandomize = useCallback(() => {
    const randomCrew = buildRandomCrew(ordersByRarity);
    if (!randomCrew) return;
    stopTimeRunningOutSound();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setOfferedOrders([]);
    setSkeletonCrew(randomCrew);
  }, [ordersByRarity]);

  /** Cost distribution for skeleton crew (0..12), same as Strategy.js cost bars */
  const skeletonCrewCostCounts = useMemo(() => {
    const counts = [...Array(13).keys()].map(() => 0);
    const orderById = new Map((orders || []).map(o => [o.id, o]));
    skeletonCrew.forEach(id => {
      const order = orderById.get(id);
      if (order != null && typeof order.cost === 'number' && order.cost >= 0 && order.cost <= 12) {
        counts[order.cost] += 1;
      }
    });
    return counts;
  }, [orders, skeletonCrew]);

  const offerNextThree = useCallback(() => {
    pickedThisRoundRef.current = false;
    const countInCrew = {};
    skeletonCrew.forEach(id => { countInCrew[id] = (countInCrew[id] || 0) + 1; });
    const canAdd = (order) => {
      const count = countInCrew[order.id] || 0;
      const max = order.is_unique === true ? 1 : 3;
      return count < max;
    };
    const filterByCap = (arr) => (arr || []).filter(canAdd);
    const byRarity = {
      common: filterByCap(ordersByRarity.common),
      rare: filterByCap(ordersByRarity.rare),
      epic: filterByCap(ordersByRarity.epic),
      legendary: filterByCap(ordersByRarity.legendary)
    };
    const totalOfferable = byRarity.common.length + byRarity.rare.length + byRarity.epic.length + byRarity.legendary.length;
    const totalInRarity = (ordersByRarity.common?.length || 0) + (ordersByRarity.rare?.length || 0) + (ordersByRarity.epic?.length || 0) + (ordersByRarity.legendary?.length || 0);
    if (totalOfferable === 0) {
      if (totalInRarity > 0) {
        offerablePoolEmptyRef.current = true;
      }
      setOfferedOrders([]);
      return;
    }
    offerablePoolEmptyRef.current = false;
    const three = [];
    const usedIds = new Set();
    for (let i = 0; i < 3; i++) {
      let order = pickByRarity(byRarity);
      let attempts = 0;
      while (order && usedIds.has(order.id) && attempts < 20) {
        order = pickByRarity(byRarity);
        attempts++;
      }
      if (!order) break;
      usedIds.add(order.id);
      three.push(order);
    }
    setOfferedOrders(three);
    setTimeLeft(PICK_TIME_MS / 1000);
    timeRunningOutPlayedRef.current = false;
    timeRunningOutAudioRef.current = null;
  }, [ordersByRarity, skeletonCrew]);

  useEffect(() => {
    if (loading || orders.length === 0 || skeletonCrew.length >= SKELETON_CREW_SIZE) return;
    if (offeredOrders.length === 0) {
      if (offerablePoolEmptyRef.current) return;
      offerNextThree();
      return;
    }
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0.1) return 0;
        return Math.max(0, prev - 0.1);
      });
    }, 100);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loading, orders.length, skeletonCrew.length, offeredOrders.length, offerNextThree]);

  useEffect(() => {
    if (timeLeft <= 0 || timeLeft > 3) return;
    if (!timeRunningOutPlayedRef.current) {
      timeRunningOutPlayedRef.current = true;
      if (typeof document !== 'undefined' && document.hidden) return;
      const v = getEffectiveSoundOutputVolume(1);
      if (v === null) return;
      try {
        const audio = new Audio('/sounds/time_running_out.mp3');
        audio.volume = v;
        timeRunningOutAudioRef.current = audio;
        audio.play().catch(() => {});
      } catch (_) {}
    }
  }, [timeLeft]);

  useEffect(() => {
    if (timeLeft > 0 || offeredOrders.length === 0 || pickedThisRoundRef.current) return;
    pickedThisRoundRef.current = true;
    stopTimeRunningOutSound();
    playSoundEffect('/sounds/mulligan_enable.mp3');
    const randomIndex = Math.floor(Math.random() * offeredOrders.length);
    const chosen = offeredOrders[randomIndex];
    if (chosen) {
      const cardEl = offerRefs.current[chosen.id];
      const counterEl = counterRef.current;
      if (cardEl && counterEl) {
        const rect = cardEl.getBoundingClientRect();
        const counterRect = counterEl.getBoundingClientRect();
        const toX = counterRect.left + counterRect.width / 2;
        const toY = counterRect.top + counterRect.height / 2;
        setFlyAnimation({
          from: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
          to: { x: toX, y: toY },
          order: chosen
        });
      }
      setAnimatingOrderId(chosen.id);
      setSkeletonCrew(prev => [...prev, chosen.id]);
      setTimeout(() => {
        setFlyAnimation(null);
        setAnimatingOrderId(null);
        setOfferedOrders([]);
      }, 600);
    }
  }, [timeLeft, offeredOrders]);

  const stopTimeRunningOutSound = () => {
    if (timeRunningOutAudioRef.current) {
      try {
        timeRunningOutAudioRef.current.pause();
        timeRunningOutAudioRef.current.currentTime = 0;
      } catch (_) {}
      timeRunningOutAudioRef.current = null;
    }
  };

  const handlePickOrder = (order, event) => {
    if (skeletonCrew.length >= SKELETON_CREW_SIZE) return;
    stopTimeRunningOutSound();
    playSoundEffect('/sounds/mulligan_enable.mp3');
    pickedThisRoundRef.current = true;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTimeLeft(0);
    const cardEl = event?.currentTarget;
    const counterEl = counterRef.current;
    if (cardEl && counterEl) {
      const rect = cardEl.getBoundingClientRect();
      const counterRect = counterEl.getBoundingClientRect();
      const toX = counterRect.left + counterRect.width / 2;
      const toY = counterRect.top + counterRect.height / 2;
      setFlyAnimation({
        from: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        to: { x: toX, y: toY },
        order
      });
    }
    setAnimatingOrderId(order.id);
    setSkeletonCrew(prev => [...prev, order.id]);
    setTimeout(() => {
      setFlyAnimation(null);
      setAnimatingOrderId(null);
      setOfferedOrders([]);
    }, 600);
  };

  const viewportWrap = (content) => {
    if (embedded) {
      return content;
    }
    return (
      <div className="skeleton-crew-viewport-wrapper">
        <div className="skeleton-crew-fullscreen-background" style={backgroundStyle} />
        <div className="skeleton-crew-viewport-content">
          {content}
        </div>
      </div>
    );
  };

  if (loading) {
    return viewportWrap(
      <div className="skeleton-crew-selection">
        <div className="skeleton-crew-loading">{t('warEffort.skeletonCrew.loadingOrders')}</div>
      </div>
    );
  }
  if (error) {
    return viewportWrap(
      <div className="skeleton-crew-selection">
        <div className="skeleton-crew-error">{error}</div>
      </div>
    );
  }
  if (orders.length === 0 || offerableCount === 0) {
    return viewportWrap(
      <div className="skeleton-crew-selection">
        <div className="skeleton-crew-error">
          {orders.length === 0 ? t('warEffort.skeletonCrew.noOrdersForFaction') : t('warEffort.skeletonCrew.noOfferableOrders')}
        </div>
      </div>
    );
  }

  if (skeletonCrew.length === SKELETON_CREW_SIZE) {
    const maxCount = Math.max(...skeletonCrewCostCounts, 1);
    const maxBarHeight = 60;
    return viewportWrap(
      <div className="skeleton-crew-selection skeleton-crew-selection--complete skeleton-crew-complete-wrap">
        <div className="skeleton-crew-complete-message">
          <h3>{t('warEffort.skeletonCrew.completeTitle')}</h3>
          <p>{t('warEffort.skeletonCrew.completeBody')}</p>
        </div>
        <div className="skeleton-crew-cost-graph-wrapper">
          <div className="skeleton-crew-cost-bar-container">
            {[...Array(13).keys()].map(cost => {
              const count = skeletonCrewCostCounts[cost];
              const barHeight = maxCount > 0 ? (count / maxCount) * maxBarHeight : 0;
              return (
                <div key={cost} className="skeleton-crew-cost-bar-segment">
                  <div
                    className="skeleton-crew-cost-bar-fill"
                    style={{ height: `${barHeight}px` }}
                    title={t('warEffort.skeletonCrew.costTitle', { cost, count })}
                  />
                  <span className="skeleton-crew-cost-bar-label">{cost}</span>
                </div>
              );
            })}
          </div>
        </div>
        {submitError && (
          <div className="skeleton-crew-error" role="alert">
            {submitError}
          </div>
        )}
        <MenuPaintedButton
          className="skeleton-crew-start-mission-button"
          imagePath={WAR_EFFORT_PRIMARY_BUTTON_PATH}
          onClick={() => {
            if (isSubmitting) return;
            onComplete(skeletonCrew);
          }}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('warEffort.skeletonCrew.starting') : t('warEffort.skeletonCrew.startMission')}
        </MenuPaintedButton>
      </div>
    );
  }

  const timerPercent = (timeLeft / (PICK_TIME_MS / 1000)) * 100;
  const isPulse = timeLeft > 0 && timeLeft <= 5;

  return viewportWrap(
    <div className={`skeleton-crew-selection skeleton-crew-selection--picking${offerLayout.compact ? ' skeleton-crew-selection--compact' : ''}`}>
      <div className="skeleton-crew-top-bar">
        <div className="skeleton-crew-header">
          <h3>{t('warEffort.skeletonCrew.selectTitle')}</h3>
          <div
            ref={counterRef}
            className="skeleton-crew-counter"
            aria-live="polite"
          >
            <span className="skeleton-crew-count-num">{skeletonCrew.length}</span>
            <span className="skeleton-crew-count-sep">/</span>
            <span className="skeleton-crew-count-total">{SKELETON_CREW_SIZE}</span>
          </div>
        </div>

        <div className={`skeleton-crew-timer-wrap ${isPulse ? 'skeleton-crew-timer-pulse' : ''}`}>
          <div
            className="skeleton-crew-timer-circle"
            style={{ '--timer-percent': timerPercent }}
          />
          <span className="skeleton-crew-timer-text">{Math.ceil(timeLeft)}s</span>
        </div>
      </div>

      <div className="skeleton-crew-pick-actions">
        <p className="skeleton-crew-instruction">
          {offerLayout.compact
            ? t('warEffort.skeletonCrew.instructionCompact')
            : t('warEffort.skeletonCrew.instruction')}
        </p>

        <div className="skeleton-crew-randomize-wrap">
          <MenuPaintedButton
            className="skeleton-crew-randomize-button"
            imagePath={WAR_EFFORT_FACTION_BUTTON_PATH}
            surfaceFilter={MENU_PAINTED_SURFACE_FILTER}
            onClick={handleRandomize}
            aria-label={t('warEffort.skeletonCrew.randomizeAria')}
          >
            {t('warEffort.skeletonCrew.randomize')}
          </MenuPaintedButton>
        </div>
      </div>

      <div
        ref={offersContainerRef}
        className={[
          'skeleton-crew-offers',
          offerLayout.stacked ? 'skeleton-crew-offers--stacked' : '',
          offerLayout.scroll ? 'skeleton-crew-offers--scroll' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          gap: offerLayout.gap,
        }}
      >
        {offeredOrders.map((order, index) => {
            const isLegendary = (order.rarity || '').toString().toLowerCase() === 'legendary';
            const legendarySoundIndex = isLegendary
              ? offeredOrders.slice(0, index).filter((o) => (o?.rarity || '').toString().toLowerCase() === 'legendary').length
              : 0;
            return (
            <div
              key={order.id}
              ref={el => { if (el) offerRefs.current[order.id] = el; }}
              className={`skeleton-crew-offer-card ${animatingOrderId === order.id ? 'skeleton-crew-card-animating' : ''}${isLegendary ? ' skeleton-crew-offer-card--legendary-vfx' : ''}`}
              style={{ width: offerLayout.cardSize, height: offerLayout.cardSize }}
              onClick={(e) => animatingOrderId == null && timeLeft > 0 && handlePickOrder(order, e)}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePickOrder(order, e); } }}
              aria-label={t('warEffort.skeletonCrew.selectOrderAria', { name: order.name })}
            >
              {isLegendary && (
                <LegendaryOrderRevealVfx
                  order={order}
                  soundDelayMs={legendarySoundIndex * 100}
                />
              )}
              <div className="skeleton-crew-offer-card-art">
                <SkeletonCrewOrderCard
                  order={order}
                  cardSize={offerLayout.cardSize}
                />
              </div>
            </div>
            );
          })}
      </div>

      <div className="skeleton-crew-cost-graph-wrapper">
        <div className="skeleton-crew-cost-bar-container">
          {[...Array(13).keys()].map(cost => {
            const count = skeletonCrewCostCounts[cost];
            const maxCount = Math.max(...skeletonCrewCostCounts, 1);
            const maxBarHeight = offerLayout.compact ? 34 : 60;
            const barHeight = maxCount > 0 ? (count / maxCount) * maxBarHeight : 0;
            return (
              <div key={cost} className="skeleton-crew-cost-bar-segment">
                <div
                  className="skeleton-crew-cost-bar-fill"
                  style={{ height: `${barHeight}px` }}
                  title={t('warEffort.skeletonCrew.costTitle', { cost, count })}
                />
                <span className="skeleton-crew-cost-bar-label">{cost}</span>
              </div>
            );
          })}
        </div>
      </div>

      {flyAnimation && (
        <div
          className="skeleton-crew-fly-clone"
          style={{
            '--fly-from-left': `${flyAnimation.from.left}px`,
            '--fly-from-top': `${flyAnimation.from.top}px`,
            '--fly-width': `${flyAnimation.from.width}px`,
            '--fly-height': `${flyAnimation.from.height}px`,
            '--fly-to-x': `${flyAnimation.to.x}px`,
            '--fly-to-y': `${flyAnimation.to.y}px`
          }}
        >
          <SkeletonCrewOrderCard
            order={flyAnimation.order}
            cardSize={offerLayout.cardSize}
          />
        </div>
      )}
    </div>
  );
}

export default WarEffortSkeletonCrewSelection;
